import React, { memo } from 'react';

import cx from 'classnames';

import { getTextSize } from './utils';
import './Text.scss';

type TitleProps<T extends keyof JSX.IntrinsicElements> = JSX.IntrinsicElements[T] & {
  Tag?: T;
};

export type TextProps<T extends keyof JSX.IntrinsicElements> = TitleProps<T> & {
  children: React.ReactNode;
  bold?: boolean;
  semibold?: boolean;
  hasMobileSize?: boolean;
  className?: string;
  style?: React.CSSProperties;
  color?: string;
};

export function HeaderXl<T extends keyof JSX.IntrinsicElements = 'h1'>({
  Tag = 'h1' as any,
  children,
  bold = false,
  semibold = false,
  hasMobileSize = true,

  className,
  style,
  ...props
}: TextProps<T>) {
  return (
    <Tag
      className={cx(
        'common-text',
        getTextSize('h-xl', hasMobileSize),
        {
          'font-bold': bold,
          'font-semibold': semibold,
        },
        className,
      )}
      style={{ color: props.color, ...style }}
      {...(props as any)}
    >
      {children}
    </Tag>
  );
}
export const HeaderXlMemo = memo(HeaderXl);

export function HeaderL<T extends keyof JSX.IntrinsicElements = 'h2'>({
  Tag = 'h2' as any,
  children,
  bold = false,
  semibold = false,
  hasMobileSize = true,

  className,
  style,
  ...props
}: TextProps<T>) {
  return (
    <Tag
      className={cx(
        'common-text',
        getTextSize('h-l', hasMobileSize),
        {
          'font-bold': bold,
          'font-semibold': semibold,
        },
        className,
      )}
      style={{ color: props.color, ...style }}
      {...(props as any)}
    >
      {children}
    </Tag>
  );
}
export const HeaderLargeMemo = memo(HeaderL);

export function HeaderM<T extends keyof JSX.IntrinsicElements = 'h3'>({
  Tag = 'h3' as any,
  children,
  className,
  bold = false,
  semibold = false,
  hasMobileSize = true,

  style,
  ...props
}: TextProps<T>) {
  return (
    <Tag
      className={cx(
        'common-text',
        getTextSize('h-m', hasMobileSize),
        { 'font-bold': bold, 'font-semibold': semibold },
        className,
      )}
      style={{ color: props.color, ...style }}
      {...(props as any)}
    >
      {children}
    </Tag>
  );
}

export const HeaderMiddleMemo = memo(HeaderM);

export function HeaderS<T extends keyof JSX.IntrinsicElements = 'div'>({
  Tag = 'div' as any,
  children,
  className,
  bold = false,
  semibold = false,
  hasMobileSize = true,
  style = {},
  ...props
}: TextProps<T>) {
  return (
    <Tag
      className={cx(
        'common-text',
        getTextSize('h-s', hasMobileSize),
        { 'font-bold': bold, 'font-semibold': semibold },
        className,
      )}
      style={{ color: props.color, ...style }}
      {...(props as any)}
    >
      {children}
    </Tag>
  );
}

export const HeaderSmallMemo = memo(HeaderS);

export function NumbersL<T extends keyof JSX.IntrinsicElements = 'div'>({
  Tag = 'div' as any,
  children,
  bold = false,
  semibold = false,
  hasMobileSize = true,

  className,
  style,
  ...props
}: TextProps<T>) {
  return (
    <Tag
      className={cx(
        'common-text',
        getTextSize('n-l', hasMobileSize),
        {
          'font-bold': bold,
          'font-semibold': semibold,
        },
        className,
      )}
      style={{ color: props.color, ...style }}
      {...(props as any)}
    >
      {children}
    </Tag>
  );
}
export const NumbersLargeMemo = memo(NumbersL);

export function NumbersM<T extends keyof JSX.IntrinsicElements = 'div'>({
  Tag = 'div' as any,
  children,
  className,
  bold = false,
  semibold = false,
  hasMobileSize = true,

  style,
  ...props
}: TextProps<T>) {
  return (
    <Tag
      className={cx(
        'common-text',
        getTextSize('n-m', hasMobileSize),
        { 'font-bold': bold, 'font-semibold': semibold },
        className,
      )}
      style={{ color: props.color, ...style }}
      {...(props as any)}
    >
      {children}
    </Tag>
  );
}

export const NumbersMiddleMemo = memo(NumbersM);

export const ParagraphXl = <T extends keyof JSX.IntrinsicElements = 'div'>({
  Tag = 'div' as any,
  children,
  bold = false,
  semibold = false,
  hasMobileSize = true,

  className,
  style,
  ...props
}: TextProps<T>) => {
  return (
    <Tag
      className={cx(
        'common-text',
        getTextSize('p-xl', hasMobileSize),
        {
          'font-bold': bold,
          'font-semibold': semibold,
        },
        className,
      )}
      style={{ color: props.color, ...style }}
      {...(props as any)}
    >
      {children}
    </Tag>
  );
};
export const ParagraphXlMemo = memo(ParagraphXl);

export const ParagraphL = <T extends keyof JSX.IntrinsicElements = 'div'>({
  Tag = 'div' as any,
  children,
  bold = false,
  semibold = false,
  hasMobileSize = true,

  className,
  style,
  ...props
}: TextProps<T>) => {
  return (
    <Tag
      className={cx(
        'common-text',
        getTextSize('p-l', hasMobileSize),
        { 'font-bold': bold, 'font-semibold': semibold },
        className,
      )}
      style={{ color: props.color, ...style }}
      {...(props as any)}
    >
      {children}
    </Tag>
  );
};
export const ParagraphLargeMemo = memo(ParagraphL);

export const ParagraphM = <T extends keyof JSX.IntrinsicElements = 'div'>({
  Tag = 'div' as any,
  children,
  bold = false,
  semibold = false,
  hasMobileSize = true,

  className,
  style,
  ...props
}: TextProps<T>) => {
  return (
    <Tag
      className={cx(
        'common-text',
        getTextSize('p-m', hasMobileSize),
        { 'font-bold': bold, 'font-semibold': semibold },
        className,
      )}
      style={{ color: props.color, ...style }}
      {...(props as any)}
    >
      {children}
    </Tag>
  );
};
export const ParagraphMiddleMemo = memo(ParagraphM);

export const CapsL = <T extends keyof JSX.IntrinsicElements = 'div'>({
  Tag = 'div' as any,
  children,
  bold = false,
  semibold = false,
  hasMobileSize = true,

  className,
  style,
  ...props
}: TextProps<T>) => {
  return (
    <Tag
      className={cx(
        'common-text',
        getTextSize('c-l', hasMobileSize),
        { 'font-bold': bold, 'font-semibold': semibold },
        className,
      )}
      style={{ color: props.color, ...style }}
      {...(props as any)}
    >
      {children}
    </Tag>
  );
};
export const CapsLiddleMemo = memo(CapsL);

export const CapsM = <T extends keyof JSX.IntrinsicElements = 'div'>({
  Tag = 'div' as any,
  children,
  bold = false,
  semibold = false,
  hasMobileSize = true,

  className,
  style,
  ...props
}: TextProps<T>) => {
  return (
    <Tag
      className={cx(
        'common-text',
        getTextSize('c-m', hasMobileSize),
        { 'font-bold': bold, 'font-semibold': semibold },
        className,
      )}
      style={{ color: props.color, ...style }}
      {...(props as any)}
    >
      {children}
    </Tag>
  );
};
export const CapsMiddleMemo = memo(CapsM);

export const CapsS = <T extends keyof JSX.IntrinsicElements = 'div'>({
  Tag = 'div' as any,
  children,
  bold = false,
  semibold = false,
  hasMobileSize = true,

  className,
  style,
  ...props
}: TextProps<T>) => {
  return (
    <Tag
      className={cx(
        'common-text',
        getTextSize('c-s', hasMobileSize),
        { 'font-bold': bold, 'font-semibold': semibold },
        className,
      )}
      style={{ color: props.color, ...style }}
      {...(props as any)}
    >
      {children}
    </Tag>
  );
};
export const CapsSmallMemo = memo(CapsS);

export const CapsXs = <T extends keyof JSX.IntrinsicElements = 'div'>({
  Tag = 'div' as any,
  children,
  bold = false,
  semibold = false,
  hasMobileSize = true,

  className,
  style,
  ...props
}: TextProps<T>) => {
  return (
    <Tag
      className={cx(
        'common-text',
        getTextSize('c-xs', hasMobileSize),
        { 'font-bold': bold, 'font-semibold': semibold },
        className,
      )}
      style={{ color: props.color, ...style }}
      {...(props as any)}
    >
      {children}
    </Tag>
  );
};
export const CapsCsmallMemo = memo(CapsXs);
